import { useState, useEffect, useRef } from 'react'
import { 
  Building2, 
  Shield, 
  Upload, 
  X, 
  Check, 
  AlertCircle, 
  FileText, 
  Plus,
  Trash2,
  Download
} from 'lucide-react'
import { toast } from 'react-toastify'
import apiService from '../../services/api'
import { useAuthStore } from '../../store/authStore'

// Import types from shared types
interface GovVendorProfile {
  isGovCertified: boolean
  cageCode?: string
  dunsNumber?: string
  gsaScheduleId?: string
  govCertifications: string[]
  approvedAgencies: string[]
  govDocsUrls: string[]
}

interface UpdateGovVendorProfileRequest {
  isGovCertified: boolean
  govStatus?: 'none' | 'pending' | 'approved' | 'rejected'
  cageCode?: string
  dunsNumber?: string
  gsaScheduleId?: string
  govCertifications: string[]
  approvedAgencies: string[]
  govDocsUrls?: string[]
}

// Validation helpers
const isValidCAGECode = (code: string): boolean => {
  return /^[A-Z0-9]{5}$/.test(code.toUpperCase())
}

const isValidDUNSNumber = (duns: string): boolean => {
  return /^\d{9}$/.test(duns.replace(/[-\s]/g, ''))
}

const isValidGSAScheduleId = (id: string): boolean => {
  return /^[A-Z0-9\-]+$/i.test(id) && id.length >= 3
}

const formatDUNSNumber = (duns: string): string => {
  const cleaned = duns.replace(/[-\s]/g, '')
  if (cleaned.length === 9) {
    return `${cleaned.substring(0, 2)}-${cleaned.substring(2, 5)}-${cleaned.substring(5, 9)}`
  }
  return duns
}

const getCertificationDisplayName = (cert: string): string => {
  const certMap: { [key: string]: string } = {
    '8(a)': 'SBA 8(a) Business Development',
    'HUBZone': 'Historically Underutilized Business Zone',
    'WOSB': 'Women-Owned Small Business',
    'EDWOSB': 'Economically Disadvantaged WOSB',
    'VOSB': 'Veteran-Owned Small Business',
    'SDVOSB': 'Service-Disabled Veteran-Owned Small Business',
    'MBE': 'Minority Business Enterprise',
    'DBE': 'Disadvantaged Business Enterprise',
    'SDB': 'Small Disadvantaged Business',
    'AbilityOne': 'AbilityOne Program',
    'GSA Schedule': 'GSA Schedule Contract',
    'SEWP': 'Solutions for Enterprise-Wide Procurement',
    'CIO-SP3': 'CIO-Solutions and Partners 3',
  }
  return certMap[cert] || cert
}

const getAgencyDisplayName = (agency: string): string => {
  const agencyMap: { [key: string]: string } = {
    'GSA': 'General Services Administration',
    'DOD': 'Department of Defense',
    'DOE': 'Department of Energy',
    'DHS': 'Department of Homeland Security',
    'VA': 'Department of Veterans Affairs',
    'NASA': 'National Aeronautics and Space Administration',
    'EPA': 'Environmental Protection Agency',
    'USDA': 'United States Department of Agriculture',
    'State of NY': 'New York State Government',
    'State of CA': 'California State Government',
    'State of TX': 'Texas State Government',
    'State of FL': 'Florida State Government',
    'NYC': 'New York City Government',
    'LA County': 'Los Angeles County Government',
    'Chicago': 'City of Chicago Government',
  }
  return agencyMap[agency] || agency
}

interface GovProfileFormProps {
  onSave?: () => void
}

// Common government certifications for dropdown
const COMMON_CERTIFICATIONS = [
  '8(a)',
  'HUBZone',
  'WOSB',
  'EDWOSB',
  'VOSB',
  'SDVOSB',
  'MBE',
  'DBE',
  'SDB',
  'AbilityOne',
  'GSA Schedule',
  'SEWP',
  'CIO-SP3'
]

// Common government agencies for dropdown
const COMMON_AGENCIES = [
  'GSA',
  'DOD',
  'DOE',
  'DHS',
  'VA',
  'NASA',
  'EPA',
  'USDA',
  'State of NY',
  'State of CA',
  'State of TX',
  'State of FL',
  'NYC',
  'LA County',
  'Chicago'
]

interface UploadedFile {
  id: string
  name: string
  url: string
  size: number
  type: string
  uploadedAt: Date
}

export default function GovProfileForm({ onSave }: GovProfileFormProps) {
  const { user } = useAuthStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // Form state
  const [formData, setFormData] = useState<GovVendorProfile>({
    isGovCertified: false,
    cageCode: '',
    dunsNumber: '',
    gsaScheduleId: '',
    govCertifications: [],
    approvedAgencies: [],
    govDocsUrls: []
  })
  
  // UI state
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [newCertification, setNewCertification] = useState('')
  const [newAgency, setNewAgency] = useState('')
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])

  useEffect(() => {
    fetchGovProfile()
  }, [])

  const fetchGovProfile = async () => {
    try {
      setLoading(true)
      const response = await apiService.get(`/api/company-profile/${user?.id}`) as any
      
      if (response.success && response.data) {
        const govData = response.data
        setFormData({
          isGovCertified: govData.isGovCertified || false,
          cageCode: govData.cageCode || '',
          dunsNumber: govData.dunsNumber || '',
          gsaScheduleId: govData.gsaScheduleId || '',
          govCertifications: govData.govCertifications || [],
          approvedAgencies: govData.approvedAgencies || [],
          govDocsUrls: govData.govDocsUrls || []
        })
        
        // Parse uploaded files from URLs
        if (govData.govDocsUrls && govData.govDocsUrls.length > 0) {
          const files = govData.govDocsUrls.map((url: string, index: number) => ({
            id: `existing-${index}`,
            name: url.split('/').pop() || `Document ${index + 1}`,
            url: url,
            size: 0,
            type: 'application/pdf',
            uploadedAt: new Date()
          }))
          setUploadedFiles(files)
        }
      }
    } catch (error) {
      console.error('Error fetching government profile:', error)
      toast.error('Failed to load government certification data')
    } finally {
      setLoading(false)
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (formData.isGovCertified) {
      // Validate CAGE Code
      if (formData.cageCode && !isValidCAGECode(formData.cageCode)) {
        newErrors.cageCode = 'CAGE Code must be 5 alphanumeric characters'
      }

      // Validate DUNS Number
      if (formData.dunsNumber && !isValidDUNSNumber(formData.dunsNumber)) {
        newErrors.dunsNumber = 'DUNS Number must be 9 digits'
      }

      // Validate GSA Schedule ID
      if (formData.gsaScheduleId && !isValidGSAScheduleId(formData.gsaScheduleId)) {
        newErrors.gsaScheduleId = 'Invalid GSA Schedule ID format'
      }

      // Require at least one certification if gov certified
      if (formData.govCertifications.length === 0) {
        newErrors.govCertifications = 'At least one government certification is required'
      }

      // Require at least one approved agency if gov certified
      if (formData.approvedAgencies.length === 0) {
        newErrors.approvedAgencies = 'At least one approved agency is required'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the validation errors before saving')
      return
    }

    try {
      setSaving(true)
      
      const requestData: UpdateGovVendorProfileRequest = {
        isGovCertified: false, // Always false when vendor applies - only admin approval sets to true
        govStatus: formData.isGovCertified ? 'pending' : 'none',
        cageCode: formData.cageCode || undefined,
        dunsNumber: formData.dunsNumber || undefined,
        gsaScheduleId: formData.gsaScheduleId || undefined,
        govCertifications: formData.govCertifications,
        approvedAgencies: formData.approvedAgencies,
        govDocsUrls: uploadedFiles.map(file => file.url)
      }

      const response = await apiService.put(
        `/api/company-profile/${user?.id}`, 
        requestData
      ) as any
      
      if (response.success) {
        toast.success('Government certification profile updated successfully!')
        onSave?.()
      } else {
        throw new Error(response.message || 'Failed to update profile')
      }
    } catch (error: any) {
      console.error('Error saving government profile:', error)
      toast.error(error.response?.data?.message || 'Failed to save government certification profile')
    } finally {
      setSaving(false)
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    try {
      setUploading(true)
      
      for (const file of Array.from(files)) {
        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
        if (!allowedTypes.includes(file.type)) {
          toast.error(`${file.name}: Only PDF and image files are allowed`)
          continue
        }

        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: File size must be less than 10MB`)
          continue
        }

        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', 'government-document')

        const response = await apiService.post('/api/upload/quote-documents', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }) as any

        if (response.success && response.data) {
          const newFile: UploadedFile = {
            id: `upload-${Date.now()}-${Math.random()}`,
            name: file.name,
            url: response.data.documentUrl,
            size: file.size,
            type: file.type,
            uploadedAt: new Date()
          }
          
          setUploadedFiles(prev => [...prev, newFile])
          toast.success(`${file.name} uploaded successfully`)
        }
      }
    } catch (error: any) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file')
    } finally {
      setUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId))
    toast.success('File removed')
  }

  const addCertification = () => {
    const cert = newCertification.trim()
    if (cert && !formData.govCertifications.includes(cert)) {
      setFormData(prev => ({
        ...prev,
        govCertifications: [...prev.govCertifications, cert]
      }))
      setNewCertification('')
      // Clear certification error if it exists
      if (errors.govCertifications) {
        setErrors(prev => ({ ...prev, govCertifications: '' }))
      }
    }
  }

  const removeCertification = (cert: string) => {
    setFormData(prev => ({
      ...prev,
      govCertifications: prev.govCertifications.filter(c => c !== cert)
    }))
  }

  const addAgency = () => {
    const agency = newAgency.trim()
    if (agency && !formData.approvedAgencies.includes(agency)) {
      setFormData(prev => ({
        ...prev,
        approvedAgencies: [...prev.approvedAgencies, agency]
      }))
      setNewAgency('')
      // Clear agency error if it exists
      if (errors.approvedAgencies) {
        setErrors(prev => ({ ...prev, approvedAgencies: '' }))
      }
    }
  }

  const removeAgency = (agency: string) => {
    setFormData(prev => ({
      ...prev,
      approvedAgencies: prev.approvedAgencies.filter(a => a !== agency)
    }))
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading government certification profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Shield className="h-6 w-6 text-green-600 mr-3" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Government Certification</h2>
            <p className="text-sm text-gray-600">Manage your government contracting credentials and certifications</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Check className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="space-y-8">
        {/* Government Certified Toggle */}
        <div className="border rounded-lg p-4 bg-green-50">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isGovCertified"
              checked={formData.isGovCertified}
              onChange={(e) => setFormData(prev => ({ ...prev, isGovCertified: e.target.checked }))}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            
            <label htmlFor="isGovCertified" className="ml-3 flex items-center cursor-pointer">
              <Building2 className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-lg font-medium text-gray-900">
                I am a government-certified vendor
              </span>
              {formData.isGovCertified && (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <Check className="h-3 w-3 mr-1" />
                  Certified
                </span>
              )}
            </label>
          </div>
          <p className="mt-2 text-sm text-gray-600 ml-7">
            {formData.isGovCertified 
              ? "✅ Government certification enabled. Fill in the details below and save to complete your application."
              : "Check this box if your company is certified to work with government agencies"
            }
          </p>
        </div>

        {/* Government Certification Fields - Only show if certified */}
        {formData.isGovCertified && (
          <>
            {/* Government IDs Section */}
            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 text-blue-600 mr-2" />
                Government Identification Numbers
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* CAGE Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CAGE Code
                  </label>
                  <input
                    type="text"
                    value={formData.cageCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, cageCode: e.target.value.toUpperCase() }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.cageCode ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter 5-character CAGE code"
                    maxLength={5}
                  />
                  {errors.cageCode && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.cageCode}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Commercial and Government Entity Code
                  </p>
                </div>

                {/* DUNS Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    DUNS Number
                  </label>
                  <input
                    type="text"
                    value={formData.dunsNumber}
                    onChange={(e) => {
                      const formatted = formatDUNSNumber(e.target.value)
                      setFormData(prev => ({ ...prev, dunsNumber: formatted }))
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.dunsNumber ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter 9-digit DUNS number"
                    maxLength={11} // Allow for dashes
                  />
                  {errors.dunsNumber && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.dunsNumber}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Data Universal Numbering System
                  </p>
                </div>

                {/* GSA Schedule ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GSA Schedule ID
                  </label>
                  <input
                    type="text"
                    value={formData.gsaScheduleId}
                    onChange={(e) => setFormData(prev => ({ ...prev, gsaScheduleId: e.target.value.toUpperCase() }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.gsaScheduleId ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter GSA Schedule ID"
                  />
                  {errors.gsaScheduleId && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.gsaScheduleId}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    General Services Administration Schedule
                  </p>
                </div>
              </div>
            </div>

            {/* Certifications Section */}
            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Shield className="h-5 w-5 text-green-600 mr-2" />
                Government Certifications
                {errors.govCertifications && (
                  <span className="ml-2 text-sm text-red-600">*Required</span>
                )}
              </h3>
              
              {/* Add Certification */}
              <div className="flex gap-3 mb-4">
                <select
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a certification...</option>
                  {COMMON_CERTIFICATIONS.map(cert => (
                    <option key={cert} value={cert} disabled={formData.govCertifications.includes(cert)}>
                      {getCertificationDisplayName(cert)}
                    </option>
                  ))}
                </select>
                <button
                  onClick={addCertification}
                  disabled={!newCertification}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </button>
              </div>

              {/* Custom Certification Input */}
              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Or enter a custom certification..."
                />
                <button
                  onClick={addCertification}
                  disabled={!newCertification.trim()}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Custom
                </button>
              </div>

              {/* Current Certifications */}
              <div className="space-y-2">
                {formData.govCertifications.map((cert, index) => (
                  <div key={index} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-lg">
                    <span className="text-sm font-medium text-green-800">
                      {getCertificationDisplayName(cert)}
                    </span>
                    <button
                      onClick={() => removeCertification(cert)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {formData.govCertifications.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No certifications added yet</p>
                )}
              </div>
            </div>

            {/* Approved Agencies Section */}
            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Building2 className="h-5 w-5 text-blue-600 mr-2" />
                Approved Government Agencies
                {errors.approvedAgencies && (
                  <span className="ml-2 text-sm text-red-600">*Required</span>
                )}
              </h3>
              
              {/* Add Agency */}
              <div className="flex gap-3 mb-4">
                <select
                  value={newAgency}
                  onChange={(e) => setNewAgency(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select an agency...</option>
                  {COMMON_AGENCIES.map(agency => (
                    <option key={agency} value={agency} disabled={formData.approvedAgencies.includes(agency)}>
                      {getAgencyDisplayName(agency)}
                    </option>
                  ))}
                </select>
                <button
                  onClick={addAgency}
                  disabled={!newAgency}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </button>
              </div>

              {/* Custom Agency Input */}
              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={newAgency}
                  onChange={(e) => setNewAgency(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Or enter a custom agency..."
                />
                <button
                  onClick={addAgency}
                  disabled={!newAgency.trim()}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Custom
                </button>
              </div>

              {/* Current Agencies */}
              <div className="space-y-2">
                {formData.approvedAgencies.map((agency, index) => (
                  <div key={index} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg">
                    <span className="text-sm font-medium text-blue-800">
                      {getAgencyDisplayName(agency)}
                    </span>
                    <button
                      onClick={() => removeAgency(agency)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {formData.approvedAgencies.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No agencies added yet</p>
                )}
              </div>
            </div>

            {/* Document Upload Section */}
            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Upload className="h-5 w-5 text-purple-600 mr-2" />
                Government Documents
              </h3>
              
              {/* File Upload */}
              <div className="mb-6">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed w-full justify-center"
                >
                  <Upload className="h-5 w-5 text-gray-400 mr-2" />
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    'Click to upload government documents (PDF, JPG, PNG)'
                  )}
                </button>
                <p className="mt-2 text-xs text-gray-500">
                  Upload certificates, capability statements, past performance documents, etc. Max 10MB per file.
                </p>
              </div>

              {/* Uploaded Files */}
              <div className="space-y-2">
                {uploadedFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 text-gray-500 mr-2" />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{file.name}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          {file.size > 0 ? formatBytes(file.size) : 'Unknown size'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Download className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => removeFile(file.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {uploadedFiles.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No documents uploaded yet</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
} 